import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';

export interface Company { id: string; name: string; nif: string; inbox_alias: string; inboxAddress: string; tax_regime: string; accounting_system: string }

export interface Me {
  user: { id: string; email: string; name: string; mfaEnabled: boolean };
  mfaVerified: boolean;
  mfaEnrollmentRequired?: boolean;
  legal?: { currentVersion: string; acceptedVersion: string | null; pending: boolean; mustAccept: boolean };
  organization: { id: string; name: string; kind: string; plan: string; modules: string[] };
  instance?: { mode: 'produccion' | 'prueba'; trialExpiresAt?: string | null; limites?: { empresas: number; proveedoresPorEmpresa: number; facturas: number } | null };
  organizations: Array<{ id: string; name: string; kind: string; role: string }>;
  companies: Company[];
  role: string;
  roleLabel: string;
  permissions: string[];
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  companyId: string | null;          // null = todas
  company: Company | null;
  setCompanyId: (id: string | null) => void;
  refresh: () => Promise<void>;
  can: (p: string) => boolean;
  hasModule: (m: string) => boolean;
  prueba: boolean;                    // entorno de prueba: se ocultan las funciones que no se usan
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyIdState] = useState<string | null>(() => localStorage.getItem('conforme.company'));

  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>('/auth/me');
      setMe(m);
      setCompanyIdState((cur) => {
        if (cur && m.companies.some((c) => c.id === cur)) return cur;
        return m.companies.length === 1 ? m.companies[0].id : null;
      });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) setMe(null);
      else throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setCompanyId = (id: string | null) => {
    setCompanyIdState(id);
    if (id) localStorage.setItem('conforme.company', id); else localStorage.removeItem('conforme.company');
  };

  const value: SessionValue = {
    me, loading, companyId, setCompanyId, refresh,
    company: me?.companies.find((c) => c.id === companyId) ?? null,
    can: (p) => Boolean(me?.permissions.includes(p)),
    hasModule: (m) => Boolean(me?.organization.modules.includes(m)),
    prueba: me?.instance?.mode === 'prueba',
    logout: async () => { await api.post('/auth/logout'); setMe(null); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider ausente');
  return v;
}
